import _ from 'lodash';
import moment from 'moment-timezone';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FakeLink } from '@/core/FakeLink';
import TimeZoneSelector from '@/core/TimeZoneSelector.molecule';
import { getTimeRounded } from '@/datetime/dateTime.utilities';
import { AutoUpdateTimeScheduleProperties } from '@/schedule/AutoUpdateTimeScheduleProperties.molecule';
import { determineInvalidEntries } from '@/schedule/schedule.utilities';
import type { Timezone } from '@/datetime/timezone.service';
import type { AutoUpdateTimeScheduleEntry, ScheduleType } from '@/schedule/schedule.types';

interface AutoUpdateTimeScheduleProps {
  timezone?: Timezone;
  setTimezone?: (timezone: Timezone) => void;
  entries?: AutoUpdateTimeScheduleEntry[];
  setEntries: (entries: AutoUpdateTimeScheduleEntry[]) => void;
  disabled?: boolean;
  minimumTimeEntryIntervalMinutes?: number;
  selectedScheduleType?: ScheduleType;
}

export const AutoUpdateTimeSchedule = ({
  timezone,
  setTimezone = _.noop,
  entries,
  setEntries,
  disabled = false,
  minimumTimeEntryIntervalMinutes,
  selectedScheduleType,
}: AutoUpdateTimeScheduleProps) => {
  const sortByLocalTime = (entries: AutoUpdateTimeScheduleEntry[]) => {
    return _.chain(entries).sortBy(['time']).value();
  };

  const setAllEntries = (entries: AutoUpdateTimeScheduleEntry[]) => {
    setEntries(entries);
    setLocalTimeEntries(entries);
  };

  const onEntryChange = (targetIndex: number, updatedEntry: AutoUpdateTimeScheduleEntry) => {
    const updatedEntries = _.map(localTimeEntries, (existingEntry, index) =>
      index === targetIndex ? updatedEntry : existingEntry,
    );
    setAllEntries(updatedEntries);
  };

  const insertDefaultTimeEntry = () => {
    const updatedEntries = [
      ..._.cloneDeep(localTimeEntries),
      {
        time: getTimeRounded(moment().subtract(15, 'minutes'), timezone),
      },
    ];
    setAllEntries(updatedEntries);
  };

  const removeTimeEntry = (targetIndex: number) => {
    const updatedEntries = localTimeEntries.filter((entry, index) => index !== targetIndex);
    setAllEntries(updatedEntries);
  };

  const { t } = useTranslation();
  const [localTimeEntries, setLocalTimeEntries] = useState(sortByLocalTime(entries as AutoUpdateTimeScheduleEntry[]));

  const isEntryInvalid = determineInvalidEntries(localTimeEntries, {
    minimumIntervalMinutes: minimumTimeEntryIntervalMinutes,
    selectedScheduleType,
  });

  return (
    <div className="flexRowContainer tw:mb-sq-15" data-testid="autoUpdateTimeSchedule">
      <div className="flexColumnContainer flexNoShrink tw:mr-sq-15">
        <div className="flexRowContainer">
          <span className="reportAutoUpdateTimeStrategy tw:mr-sq-5">
            {t('REPORT.MODAL.AUTO_UPDATE.TIME.STRATEGIES.UPDATE_AT')}
          </span>
          {_.map(localTimeEntries, (entry, index) => (
            <AutoUpdateTimeScheduleProperties
              key={`entry${index}`}
              isInvalid={isEntryInvalid[index]}
              showRemoveIcon={localTimeEntries.length > 1}
              entry={entry}
              onChange={(entry) => onEntryChange(index, entry)}
              onClickRemove={() => removeTimeEntry(index)}
              disabled={disabled}
            />
          ))}
          {/* When a minimum interval is enforced (custom agents), name the specific problem next to
              the offending fields instead of relying on an always-visible help line. */}
          {minimumTimeEntryIntervalMinutes && _.some(isEntryInvalid) ? (
            <div data-testid="time-schedule-min-interval-error" className="sq-text-danger tw:mb-sq-15">
              {t('SCHEDULE_TYPE.MIN_INTERVAL_VIOLATION')}
            </div>
          ) : null}
        </div>

        {timezone ? (
          <div className="flexRowContainer ml50 tw:mb-sq-15">
            <span className="reportAutoUpdateTimeStrategy tw:mr-sq-5">
              {t('REPORT.MODAL.AUTO_UPDATE.TIME.TIME_ZONE')}
            </span>
            <TimeZoneSelector
              extraClassNames="width-220"
              timezone={timezone}
              onSelect={setTimezone}
              disabled={disabled}
            />
          </div>
        ) : null}
      </div>
      {!disabled ? (
        <label>
          <FakeLink extraClassNames="link-no-focus link-no-underline" onClick={insertDefaultTimeEntry}>
            {t('REPORT.MODAL.AUTO_UPDATE.TIME.ADD_ANOTHER_TIME')}
          </FakeLink>
        </label>
      ) : null}
    </div>
  );
};
